import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import { AuthService } from '@lmc-app/core/services/auth.service';
import { UserService } from '@lmc-app/core/services/user.service';
import { TokenStorageService } from '@lmc-app/core/services/token-storage.service';
import { NotificationService } from '@lmc-app/core/services/notification.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import { fetchPermissions, selectReturnUrl, selectUser, State, } from '@lmc-app/store';

/**
 * Auth Effects
 *
 * Handles side effects for authentication actions (API calls, navigation).
 */
@Injectable()
export class AuthEffects {
	/**
	 * Login Effect
	 *
	 * Flow:
	 * 1. Listen for login action
	 * 2. Call backend login API with credentials
	 * 3. Backend returns tokens and user data in response body
	 * 4. Save tokens to localStorage
	 * 5. Dispatch loginSuccess with user data and tokens
	 * 6. If login fails, dispatch loginFailure with error
	 */
	login$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthActions.login),
			switchMap((action) => {
				return this.authService.login(action.credentials).pipe(
					map((response: { sessionExpiresAt: string; user: User; accessToken: string; refreshToken: string }) => {
						// Save tokens to localStorage for persistence across page refreshes
						this.tokenStorage.saveTokens(response.accessToken, response.refreshToken);
						return AuthActions.loginSuccess({
							user: response.user,
							accessToken: response.accessToken,
							refreshToken: response.refreshToken,
						});
					}),
					catchError((error) => {
						return of(AuthActions.loginFailure({ error }));
					})
				);
			})
		)
	);

	/**
	 * Login Success Effect
	 *
	 * Flow:
	 * 1. Listen for loginSuccess action
	 * 2. Dispatch fetchPermissions to load user permissions
	 * 3. Wait for BOTH user state and returnUrl to be available in store
	 * 4. Navigate to returnUrl (if valid) or /dashboard (default)
	 * 5. Clear returnUrl from state
	 */
	loginSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AuthActions.loginSuccess),
				tap(() => {
					this.store.dispatch(fetchPermissions());
				}),
				switchMap(() => {
					return combineLatest([
						this.store.select(selectUser),
						this.store.select(selectReturnUrl)
					]).pipe(
						filter(([user]) => user !== null),
						take(1)
					);
				}),
				tap(([user, returnUrl]) => {
					console.log('[Auth] Login navigation:', {
						user: user?.email,
						returnUrl,
						targetUrl: this.getSafeReturnUrl(returnUrl)
					});

					const targetUrl = this.getSafeReturnUrl(returnUrl);
					this.router.navigate([targetUrl]);
					this.store.dispatch(AuthActions.clearReturnUrl());
				})
			),
		{ dispatch: false }
	);

	/**
	 * Logout Effect — Instant Logout
	 *
	 * Flow:
	 * 1. Listen for logout action
	 * 2. Clear tokens from localStorage
	 * 3. Redirect to /login
	 *
	 * No API call needed — tokens are stateless JWTs. Clearing localStorage
	 * is sufficient to end the session. State cleanup is handled by reducers.
	 */
	logout$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AuthActions.logout),
				tap(() => {
					this.tokenStorage.clearTokens();
					this.router.navigate(['/login'], { replaceUrl: true });
				})
			),
		{ dispatch: false }
	);

	/**
	 * Initialize Session Effect
	 *
	 * Flow:
	 * 1. Listen for initializeSession action (dispatched by authGuard on first protected route access)
	 * 2. Check if tokens exist in localStorage
	 * 3. If no tokens, immediately dispatch initializeSessionFailure
	 * 4. If tokens exist, call /v2/users/me to validate session
	 * 5. If 401 (expired), interceptor automatically tries to refresh token via localStorage refresh token
	 * 6. If refresh succeeds, retry /v2/users/me
	 * 7. If refresh fails, interceptor dispatches logout
	 */
	initializeSession$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AuthActions.initializeSession),
			switchMap(() => {
				if (!this.tokenStorage.hasTokens()) {
					return of(AuthActions.initializeSessionFailure({ error: 'No tokens in storage' }));
				}

				return this.userService.getUserMe().pipe(
					map((user: User) => {
						return AuthActions.initializeSessionSuccess({ user });
					}),
					catchError((error) => {
						return of(AuthActions.initializeSessionFailure({ error }));
					})
				);
			})
		)
	);

	/**
	 * Initialize Session Success Effect
	 */
	initializeSessionSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AuthActions.initializeSessionSuccess),
				tap(() => {
					console.log('Session initialized successfully');
					this.store.dispatch(fetchPermissions());
				})
			),
		{ dispatch: false }
	);

	/**
	 * Initialize Session Failure Effect
	 */
	initializeSessionFailure$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AuthActions.initializeSessionFailure),
				tap(() => {
					console.log('Session initialization failed - user not authenticated');
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private authService: AuthService,
		private userService: UserService,
		private tokenStorage: TokenStorageService,
		private notificationService: NotificationService,
		private router: Router,
		private store: Store<State>
	) {}

	private getSafeReturnUrl(returnUrl: string | null): string {
		if (!returnUrl) return '/dashboard';
		if (!returnUrl.startsWith('/')) return '/dashboard';
		if (returnUrl.startsWith('/login')) return '/dashboard';
		return returnUrl;
	}
}